import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEnrichmentStatus } from '@/hooks/useEnrichmentStatus';
import { Card, ProgressStepper, SkeletonCard, Button } from '@/components/ui';
import { useT } from '@/i18n/LanguageContext';

export default function EnrichingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useEnrichmentStatus(id);
  const t = useT();

  const STEP_LABELS = [
    t.enriching.steps.ocr,
    t.enriching.steps.parsing,
    t.enriching.steps.verifying,
    t.enriching.steps.credit,
    t.enriching.steps.risk,
    t.enriching.steps.report,
  ];

  // Navigate on completion
  useEffect(() => {
    if (data?.status === 'completed' && data.leadId) {
      const timer = setTimeout(() => {
        navigate(`/leads/${data.leadId}`, { replace: true });
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [data?.status, data?.leadId, navigate]);

  const steps = STEP_LABELS.map((label, i) => {
    const currentStep = data?.currentStep ?? 0;
    return {
      label,
      status: i < currentStep ? 'completed' as const : i === currentStep && data?.status === 'running' ? 'active' as const : 'pending' as const,
    };
  });

  // Mark all as completed when done
  if (data?.status === 'completed') {
    steps.forEach((s) => (s.status = 'completed'));
  }

  return (
    <div className="p-4 md:p-6 max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-reevol-blue to-reevol-cyan flex items-center justify-center">
          {data?.status === 'completed' ? (
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : data?.status === 'failed' ? (
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
        </div>
        <h1 className="text-xl font-bold text-reevol-text">
          {data?.status === 'completed'
            ? t.enriching.checkComplete
            : data?.status === 'failed'
              ? t.enriching.checkFailed
              : t.enriching.running}
        </h1>
        <p className="text-sm text-reevol-text-muted mt-1">
          {data?.status === 'completed'
            ? t.enriching.redirecting
            : data?.status === 'failed'
              ? data.errorMessage || t.enriching.errorDefault
              : t.enriching.analyzing}
        </p>
      </div>

      {/* Progress stepper */}
      <Card className="p-5 mb-6">
        {isLoading ? (
          <SkeletonCard />
        ) : (
          <ProgressStepper steps={steps} />
        )}
      </Card>

      {/* Failure actions */}
      {data?.status === 'failed' && (
        <div className="flex gap-3">
          <Button variant="secondary" fullWidth onClick={() => navigate('/leads')}>
            {t.enriching.viewLeads}
          </Button>
          <Button fullWidth onClick={() => navigate('/new')}>
            {t.enriching.tryAgain}
          </Button>
        </div>
      )}

      {/* Error */}
      {error && !data && (
        <Card className="p-5 text-center">
          <p className="text-sm text-red-600 mb-3">{t.enriching.failedStatus}</p>
          <Button variant="secondary" onClick={() => navigate('/leads')}>
            {t.common.backToLeads}
          </Button>
        </Card>
      )}

      {/* Preview skeletons */}
      {data?.status !== 'completed' && data?.status !== 'failed' && (
        <div className="space-y-3 opacity-40">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}
    </div>
  );
}
